import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CategoryBarChart,
  ChartCard,
  DailySeriesChart,
  formatCount,
  formatCredits,
  formatUsd,
  CHART_SEMANTIC,
} from '@kioskgaming/ui';
import CreditPurchaseTrigger from '../credits/CreditPurchaseTrigger';
import GaugeChart from './GaugeChart';
import HeadlineCard from './HeadlineCard';
import MetricStrip from './MetricStrip';
import { cashSettlementView } from './chartHelpers';
import type { AgentDashboardOverviewData, DashboardV2Period } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  costRate: number;
  defaultOpenCreditPurchase?: boolean;
  data: AgentDashboardOverviewData | null;
  isLoading: boolean;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-blue-900/70">{title}</h2>
    {children}
  </section>
);

export default function OverviewTab({
  costRate,
  defaultOpenCreditPurchase,
  data,
  isLoading,
}: Props) {
  const kpis = data?.kpis;
  const extras = data?.extras;
  const dailyFlow = data?.charts?.dailyPlayerFlow ?? [];
  const topShops = data?.charts?.topShops ?? [];
  const cash = cashSettlementView(data?.extras?.cashSettlement);

  const activityMix = [
    { name: 'Online deposits', value: kpis?.onlineDepositsNetUsd?.value ?? 0 },
    { name: 'Online withdrawals', value: kpis?.onlineWithdrawalsGrossUsd?.value ?? 0 },
    { name: 'Player money in', value: kpis?.playerMoneyIn?.value ?? 0 },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      <Section title="Headline">
        <div className="space-y-3">
          <HeadlineCard
            label="Net position"
            tooltip="Earned minus spent for the selected period (your Agent trading P&L)."
            kpi={kpis?.netPosition}
            formatValue={formatUsd}
          />
          <MetricStrip
            columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-7"
            items={[
              {
                id: 'earned',
                label: 'Earned',
                tooltip: 'USD earned selling credits to your shops.',
                kpi: kpis?.earnedTotal,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'spent',
                label: 'Spent',
                tooltip: 'USD you paid your super agent for credits.',
                kpi: kpis?.spentTotal,
                format: 'currency',
                invertDelta: true,
                barClassName: 'bg-orange-100',
                valueClassName: 'text-orange-800',
              },
              {
                id: 'playerMoneyIn',
                label: 'Player money in',
                tooltip: 'Online deposits plus counter cash from shop-linked players.',
                kpi: kpis?.playerMoneyIn,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'onlineDep',
                label: 'Online deposits',
                tooltip: 'Net USD from online player deposits.',
                kpi: kpis?.onlineDepositsNetUsd,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'onlineWd',
                label: 'Online withdrawals',
                tooltip: 'Gross USD paid out via online withdrawals.',
                kpi: kpis?.onlineWithdrawalsGrossUsd,
                format: 'currency',
                invertDelta: true,
                barClassName: 'bg-orange-100',
                valueClassName: 'text-orange-800',
              },
              {
                id: 'shops',
                label: 'Shops',
                tooltip: 'Shops under your account.',
                value: formatCount(extras?.shopsCount ?? 0),
                barClassName: 'bg-blue-100',
              },
              {
                id: 'roadblocks',
                label: 'Roadblocks',
                tooltip: 'Open blockers needing attention.',
                value: formatCount(extras?.roadblocksBadgeCount ?? 0),
                barClassName: extras?.roadblocksBadgeCount ? 'bg-orange-100' : 'bg-emerald-100',
                valueClassName: extras?.roadblocksBadgeCount ? 'text-orange-800' : 'text-emerald-800',
              },
            ]}
          />
        </div>
      </Section>

      <Section title="Wallet">
        <div className="flex flex-col gap-3 rounded-xl border border-blue-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Credits</p>
              <p className="text-lg font-semibold tabular-nums text-blue-950">
                {formatCredits(kpis?.creditBalance?.value ?? 0)}
              </p>
              <Link to="/transactions" className="text-xs font-semibold text-blue-700 hover:text-blue-900">
                Transactions
              </Link>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">USD wallet</p>
              <p className="text-lg font-semibold tabular-nums text-blue-950">
                {formatUsd(kpis?.usdBalanceNet?.value ?? 0)}
              </p>
              <Link to="/online-transactions" className="text-xs font-semibold text-blue-700 hover:text-blue-900">
                USD ledger
              </Link>
            </div>
          </div>
          <CreditPurchaseTrigger
            variant="button"
            className="w-full sm:w-auto sm:min-w-[14rem]"
            costRate={costRate}
            creditBalance={kpis?.creditBalance?.value ?? null}
            creditBalanceLoading={isLoading}
            onlineBalanceNet={kpis?.usdBalanceNet?.value ?? null}
            onlineBalanceLoading={isLoading}
            conversionCostRate={costRate}
            defaultOpen={defaultOpenCreditPurchase}
          />
        </div>
      </Section>

      <Section title="Cash flow">
        <ChartCard
          title="Player cash flow trend"
          tooltip="Player money in (online + counter) vs payouts (online withdrawals + counter redemptions)."
          loading={isLoading}
          empty={!isLoading && dailyFlow.every((d) => d.usdIn === 0 && d.payoutsOut === 0)}
        >
          <div className="h-72">
            <DailySeriesChart
              data={dailyFlow}
              xKey="date"
              kind="bar"
              height={288}
              series={[
                { key: 'usdIn', label: 'Money in', color: CHART_SEMANTIC.moneyIn },
                { key: 'payoutsOut', label: 'Payouts out', color: CHART_SEMANTIC.moneyOut },
              ]}
              valueFormatter={formatUsd}
            />
          </div>
        </ChartCard>
      </Section>

      <Section title="Network">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ChartCard
            title="Top shops by credits purchased"
            tooltip="Shops ranked by credit purchase volume in the selected period."
            loading={isLoading}
            empty={!isLoading && topShops.length === 0}
            emptyLabel="No shop purchases in this period"
          >
            <CategoryBarChart
              data={topShops.map((s) => ({ name: s.name, value: s.creditsPurchased }))}
              color={CHART_SEMANTIC.credits}
              valueFormatter={formatUsd}
            />
          </ChartCard>
          <ChartCard
            title="Network activity mix"
            tooltip="Period totals for online deposits, withdrawals, and player money in."
            loading={isLoading}
            empty={!isLoading && activityMix.length === 0}
            emptyLabel="No network player activity in this period"
          >
            <CategoryBarChart data={activityMix} color={CHART_SEMANTIC.players} valueFormatter={formatUsd} />
          </ChartCard>
          <ChartCard
            title="Cash settlement health"
            tooltip="Cash your shops owe you for credit sales this period vs cash already received."
            loading={isLoading}
            empty={!isLoading && cash.expected === 0 && cash.received === 0}
            emptyLabel="No cash settlements in this period"
          >
            <GaugeChart
              percent={cash.collectedPct}
              primaryLabel="Cash received"
              primaryValue={cash.received}
              secondaryLabel="Still uncollected"
              secondaryValue={cash.uncollected}
            />
          </ChartCard>
        </div>
      </Section>
    </div>
  );
}
